import { defineStore } from 'pinia';

// Tracks whether the *browser* is reachable on the NetBird overlay.
// Implementation is a same-origin fetch probe to a vhost that's bound to
// a NetBird mesh IP — see control-plane/nginx-snippets/netbird-probe.bazeport.net.conf.
// If the host has no route to 100.123.223.24, the fetch times out; if it
// has one (NetBird is up), the fetch resolves with status 200.

const PROBE_URL = 'https://netbird-probe.bazeport.net/healthz';
// Generous enough to survive a slow first-handshake over the relay, tight
// enough that a non-NetBird user sees the warning quickly on page load.
const PROBE_TIMEOUT_MS = 2500;
const REFRESH_INTERVAL_MS = 60_000;

export type NetbirdStatus = 'unknown' | 'online' | 'offline';

// Chrome 130+ enforces Local Network Access (LNA): fetches from a public
// origin (the portal at supportvpn.bazeport.net) to a local address
// (100.64/10 CGNAT, which is where the probe lives) require an explicit
// user permission grant via a one-time prompt. The CORS preflight header
// `Access-Control-Allow-Private-Network: true` on the probe response is
// necessary but not sufficient — if the user clicked Block on the prompt
// (or has it denied by policy), the fetch still fails with the same
// "blocked by CORS policy: Permission was denied" wording we'd otherwise
// expect from a missing server header. Querying this permission state
// lets the UI distinguish "Chrome blocked us, here's how to fix it" from
// "actually offline, start NetBird".
//
// The Permissions API name has evolved across the PNA → LNA transition;
// we probe both. 'unsupported' = non-Chrome browser, no gate.
export type LocalNetworkPermission =
    | 'unknown'
    | 'granted'
    | 'denied'
    | 'prompt'
    | 'unsupported';

const LNA_PERMISSION_NAMES = ['local-network-access', 'private-network-access'] as const;

async function queryLocalNetworkPermission(): Promise<LocalNetworkPermission> {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) {
        return 'unsupported';
    }
    for (const name of LNA_PERMISSION_NAMES) {
        try {
            const status = await navigator.permissions.query(
                // Cast — the standard PermissionName union does not yet
                // include the LNA names in all TS lib versions.
                { name: name as PermissionName },
            );
            return status.state as LocalNetworkPermission;
        } catch {
            // Name not recognised in this browser — try the next one.
        }
    }
    return 'unsupported';
}

export const useNetbirdStore = defineStore('netbird', {
    state: () => ({
        status: 'unknown' as NetbirdStatus,
        clientIp: null as string | null,
        permissionState: 'unknown' as LocalNetworkPermission,
        checking: false,
        lastChecked: null as number | null,
        timer: null as ReturnType<typeof setInterval> | null,
    }),
    actions: {
        async probe() {
            // Single in-flight probe at a time — the polling timer can fire
            // before the previous fetch resolves on slow networks.
            if (this.checking) return;
            this.checking = true;
            const aborter = new AbortController();
            const timeoutId = setTimeout(() => aborter.abort(), PROBE_TIMEOUT_MS);
            try {
                // Refresh LNA permission alongside each probe so the banner
                // can switch between the generic offline message and the
                // Chrome-specific "blocked, here's how to fix" message.
                this.permissionState = await queryLocalNetworkPermission();

                const res = await fetch(PROBE_URL, {
                    method: 'GET',
                    mode: 'cors',
                    cache: 'no-store',
                    credentials: 'omit',
                    signal: aborter.signal,
                });
                if (!res.ok) {
                    this.status = 'offline';
                    this.clientIp = null;
                } else {
                    // Body is `{ netbird: true, clientIp: "100.x.x.x" }`.
                    // We tolerate missing fields — the response reaching us
                    // at all is the actual signal we care about.
                    try {
                        const body = await res.json();
                        this.clientIp = typeof body?.clientIp === 'string' ? body.clientIp : null;
                    } catch { this.clientIp = null; }
                    this.status = 'online';
                }
            } catch {
                // Network error, CORS rejection, timeout — all imply "not on
                // NetBird" from the user's point of view.
                this.status = 'offline';
                this.clientIp = null;
            } finally {
                clearTimeout(timeoutId);
                this.checking = false;
                this.lastChecked = Date.now();
            }
        },
        startPolling() {
            if (this.timer) return;
            this.probe();
            this.timer = setInterval(() => this.probe(), REFRESH_INTERVAL_MS);
        },
        stopPolling() {
            if (this.timer) { clearInterval(this.timer); this.timer = null; }
        },
    },
});
